import { useEffect, useRef, useState } from 'react'

// eslint-disable-next-line import/order
import { useTranslation } from 'react-i18next'

import { useParams } from 'react-router-dom'

import { getInspection } from '@/infra/http/getInspection'
import sendEvaluate from '@/infra/http/sendEvaluate'
import EvaluateBackground from '@/presentation/assets/img/EvaluateBackground.png'
import CircleInfo from '@/presentation/assets/svg/CircleInfo'
import Button from '@/presentation/components/Button'
import Header from '@/presentation/components/Header'
import KnowMoreModal from '@/presentation/pages/EvaluatePage/components/KnowMorePopup'
import StarRating from '@/presentation/pages/EvaluatePage/components/StarRating'
import SuccessGoogleModal from '@/presentation/pages/EvaluatePage/components/SuccessGoogleModal'
import SuccessModal from '@/presentation/pages/EvaluatePage/components/SuccessModal'
import { ROUTES } from '@/services/constants/routes'
import useNavigateWithOrderCode from '@/services/hooks/useNavigateWithOrderCode'
import { useInspection } from '@/services/providers/InspectionContext'
import { useUser } from '@/services/providers/UserContext'

const EvaluatePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigateWithOrderCode()

  const { user } = useUser()
  const { inspection } = useInspection()
  const { orderCode } = useParams()

  const commentInputRef = useRef<HTMLTextAreaElement>(null)

  const [showKnowMoreModal, setShowKnowMoreModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [inspector, setInspector] = useState(0)
  const [report, setReport] = useState(0)
  const [googleReviewUrl, setGoogleReviewUrl] = useState('')
  const [canOpenGoogleRating, setCanOpenGoogleRating] = useState(false)

  const handleChangeReportRating = (rating: number) => {
    if (rating === report && rating > 1) {
      setReport(rating - 1)

      return
    }

    setReport(rating)
  }

  const handleChangeInspectorRating = (rating: number) => {
    if (rating === inspector && rating > 1) {
      setInspector(rating - 1)

      return
    }

    setInspector(rating)
  }

  const handleConfirmForm = async () => {
    if (!user) {
      navigate(ROUTES.REGISTER_INITIAL)

      return
    }

    if (!inspection) {
      navigate(ROUTES.MAIN)

      return
    }
    const payload = {
      rating: {
        inspector,
        report,
        comment: commentInputRef.current?.value || ''
      },
      creator: {
        name: user.name,
        email: user.email,
        personType: user.personType
      }
    }

    const canGoogleRating = inspector >= 4 && report >= 4 && !!googleReviewUrl
    setCanOpenGoogleRating(canGoogleRating)

    await sendEvaluate(inspection.publicId, payload)

    setShowSuccessModal(true)

    if (canGoogleRating) {
      return
    }

    setTimeout(() => {
      navigate(ROUTES.MAIN)
    }, 2000)
  }

  const handleGetInspectionData = async () => {
    if (!orderCode) {
      return
    }

    try {
      const data = await getInspection(orderCode)
      setGoogleReviewUrl(data?.googleReviewUrl || '')
    } catch {
      setGoogleReviewUrl('')
    }
  }

  useEffect(() => {
    handleGetInspectionData()
  }, [])

  return (
    <div className="flex w-full flex-col items-center justify-center">
      <Header title={t('EVALUATE_YOUR_INSPECTION', 'Avalie sua vistoria')} />

      <div className="relative flex flex-col items-center px-4 py-2 sm:w-96 md:w-144">
        <span className="mb-4 block w-full text-left text-xl text-[#72767D]">
          {t('REPORT', 'Relatório')}
        </span>

        <StarRating rating={report} handleChange={handleChangeReportRating} />

        <span className="mb-4 block w-full text-left text-xl text-[#72767D]">
          {t('INSPECTOR', 'Vistoriador')}
        </span>

        <StarRating
          rating={inspector}
          handleChange={handleChangeInspectorRating}
        />

        <div className="mb-4" />

        <span className="mb-4 flex w-full items-center gap-4 text-left text-xl text-[#72767D]">
          {t('ADD_COMMENT', 'Adicionar comentário')}{' '}
          <span className="text-sm font-medium text-gray-400">
            ({t('OPTIONAL', 'Opcional')})
          </span>
        </span>

        <textarea
          className="mb-6 min-h-[82px] w-full rounded-lg border border-gray-300 p-2 text-base shadow-md focus:border-primary focus:outline-none"
          placeholder={t('DESCRIBE_EXPERIENCE', 'Descreva sua experiência...')}
          ref={commentInputRef}
        />

        <div className="mb-20 w-full text-left text-sm text-[#72767D]">
          {t(
            'THIS_FIELD_IS_EXCLUSIVELY_FOR_EVALUATING_YOUR_EXPERIENCE',
            'Esse campo é destinado exclusivamente para avaliar sua experiência.'
          )}{' '}
          <span
            onClick={() => setShowKnowMoreModal(true)}
            className="mt-2 flex cursor-pointer items-center text-primary"
          >
            <CircleInfo className="mx-1 cursor-pointer" />

            <span>{t('LEARN_MORE', 'Saiba mais')}</span>
          </span>
        </div>

        <Button
          className="mb-4 w-full max-w-[420px]"
          onClick={handleConfirmForm}
        >
          {t('EVALUATE', 'Avaliar')}
        </Button>

        <img
          className="absolute -right-80 bottom-0 -z-10 max-w-[400px]"
          src={EvaluateBackground}
          alt="Evaluate inspection"
        />
      </div>

      {showSuccessModal && canOpenGoogleRating ? (
        <SuccessGoogleModal googleReviewUrl={googleReviewUrl} />
      ) : (
        showSuccessModal && <SuccessModal />
      )}

      {showKnowMoreModal && (
        <KnowMoreModal
          inspectionType={inspection?.inspectionType}
          onCancel={() => setShowKnowMoreModal(false)}
        />
      )}
    </div>
  )
}

export default EvaluatePage
